import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { ServersService } from '../../../../core/services/servers.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, map } from 'rxjs';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-migrate-group-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    AsyncPipe,
  ],
  templateUrl: './migrate-group-dialog.component.html',
  styleUrl: './migrate-group-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MigrateGroupDialogComponent {
  readonly dialogRef = inject(MatDialogRef<MigrateGroupDialogComponent>);

  readonly data = inject<DialogData & { currentServerId: string }>(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  private serversService = inject(ServersService);

  readonly serverSearchText = signal('');

  servers$ = combineLatest([
    this.serversService.getAllServers(),
    toObservable(this.serverSearchText),
  ]).pipe(
    map(([servers, searchText]) =>
      servers
        .filter((server) => server.hostname.toLowerCase().includes(searchText.trim().toLowerCase()))
        .map((server) => ({
          label: server.hostname,
          value: server._id,
        })),
    ),
  );

  form = this.fb.nonNullable.group({
    server: [null, [Validators.required, this.serverValidator(this.data.currentServerId)]],
  });

  serverValidator(currentServerId: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const serverId = control.value;

      if (serverId === currentServerId) {
        return { forbiddenServer: true };
      }

      return null;
    };
  }

  submit(): void {
    if (this.form.invalid) return;

    this.dialogRef.close(this.form.getRawValue());
  }

  close(): void {
    this.dialogRef.close();
  }
}
